// Where the train is, said as a phrase — and the four ways it refuses to say it.
//
// PTV's pattern call already carries `vehicle_position`, mapped by the server
// into `{ lat, lng, at, expiresAt }`. This file turns that one coordinate into
// "JUST LEFT MALVERN" by projecting it onto the run's OWN pattern: the pairs of
// stations this train actually calls at, in the order it calls at them, using
// the coordinates already bundled in `src/data/network.json`.
//
// A POSITION NEVER LEAVES THE PHONE — and nothing here sends one. The train's
// coordinate arrives inside a response the app already makes, and the station
// coordinates are bundled. There is no endpoint with a coordinate parameter and
// none is to be added. (Same rule as `src/network.js`, for the same reason.)
//
// THE HALF THAT MATTERS IS REFUSING TO ANSWER. What we have is a point and a
// list of stations — not track geometry. A train on the Frankston line passing
// within 400 m of a Sandringham station is not at that station, and a phrase
// that says it is would be worse than no phrase at all, because it is the kind
// of wrong nobody can check from inside a carriage. So this returns null far
// more readily than it returns a phrase, and every threshold below is a refusal
// rather than a tolerance.

import { lineStops, metresBetween, stationById, throughContinuations } from './network.js';

/**
 * How stale a position may be before it stops being evidence.
 *
 * PTV expires them at about sixty seconds and THE APP DOES NOT POLL — a train
 * card sits open for as long as somebody is reading it, so without this a
 * coordinate from eleven minutes ago would go on drawing a marker at a station
 * the train left four stops back. Two minutes is one missed expiry, not a
 * window worth trusting: it is deliberately close to the expiry itself.
 */
export const MAX_POSITION_AGE_MS = 120_000;

/**
 * How far off a chord the point may sit and still be on it.
 *
 * We have STATION COORDINATES, NOT TRACK GEOMETRY: the straight line between
 * two stations is not the railway, and on a curve the real track wanders a few
 * hundred metres from the chord. 600 m absorbs that and still refuses a train
 * that is simply somewhere else — and it is deliberately below the 800 m at
 * which `NEAR_METRES` already declines to claim you are *near* a station. If
 * the app will not say you are at a station from 800 m away, it must not say a
 * train is between two of them from further.
 */
export const MAX_OFF_CHORD_METRES = 600;

/**
 * How much better the winner must be than a non-adjacent runner-up.
 *
 * Through the city loop and the Metro Tunnel two segments of ONE run genuinely
 * lie near-parallel — a train between Flagstaff and Melbourne Central is also
 * close to the chord it will ride twenty minutes later. Where the two best
 * segments are adjacent this is harmless: they share a station, so both
 * readings name the same piece of railway. Where they are not, the answer is a
 * coin toss and the only honest output is none.
 */
export const AMBIGUOUS_RATIO = 1.25;

/** Past the halfway mark the train is better described by where it is going. */
const JUST_LEFT_UNTIL = 0.5;

/**
 * How close to a station a train has to be to be AT it rather than between.
 *
 * A train STANDING at a platform projects to t ≈ 0 of the segment ahead of it,
 * so the card captioned a train that had just pulled into State Library "JUST
 * LEFT STATE LIBRARY" — the one reading a passenger on it can immediately see
 * is wrong, and the one that makes them stay in their seat.
 *
 * MEASURED IN METRES, NEVER IN `t`. A fraction of a segment is a different
 * distance on every segment: two per cent of Flinders Street to Southern Cross
 * is twenty metres and two per cent of Officer to Cardinia Road is ninety, and
 * a rule written in `t` would call one of them wrong whichever number it chose.
 * Eighty metres is about a platform's length — inside it the train is at the
 * station by any reading, and it is far below the 600 m at which the projection
 * will still call a point "on" the chord.
 */
export const AT_STATION_METRES = 80;

/**
 * One pattern stop's bundled coordinate, or null.
 *
 * The PATTERN says which stations this train calls at; the BUNDLE says where
 * they are. A stop the bundle has never heard of — a new station, a PTV id the
 * network file predates — has no coordinate, and there is nothing to project
 * onto. It is skipped, and crucially its neighbours are NOT joined across the
 * gap: a chord drawn from the station before it to the station after it is a
 * line through a station the train stops at, and a point near that station
 * would score as "between" the two either side of it.
 */
function coordOf(stop) {
  const lat = Number(stop?.lat);
  const lng = Number(stop?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  const station = stationById(stop?.stopId);
  if (!station) return null;
  return Number.isFinite(station.lat) && Number.isFinite(station.lng)
    ? { lat: station.lat, lng: station.lng }
    : null;
}

/** The name to say out loud: the pattern's, or the bundle's, or nothing. */
function nameOf(stop) {
  const fromPattern = String(stop?.name ?? '').trim();
  if (fromPattern) return fromPattern;
  return String(stationById(stop?.stopId)?.name ?? '').trim();
}

/**
 * A local east/north frame in metres, with `origin` at (0, 0).
 *
 * Melbourne is small enough and `metresBetween` exact enough that a flat frame
 * over a two-kilometre chord is not an approximation worth worrying about. The
 * sign comes from the raw difference, because `metresBetween` is a distance and
 * distances have no direction.
 */
function offsetMetres(origin, point) {
  const east = metresBetween(origin.lat, origin.lng, origin.lat, point.lng);
  const north = metresBetween(origin.lat, origin.lng, point.lat, origin.lng);
  return {
    x: point.lng >= origin.lng ? east : -east,
    y: point.lat >= origin.lat ? north : -north,
  };
}

/**
 * How far the point sits off the chord A→B, and how far along it.
 *
 * `t` is clamped to [0, 1], so a point beyond either end projects onto that end
 * and is measured from it — which is what makes an overshooting train read as
 * "approaching" the station it is nearly at rather than scoring absurdly well
 * against a segment it has already finished.
 */
function projectOnto(a, b, point) {
  const ab = offsetMetres(a, b);
  const ap = offsetMetres(a, point);
  const lengthSq = ab.x * ab.x + ab.y * ab.y;
  // Two stops at one coordinate are not a segment. Nothing can be "between"
  // them, and dividing by this would be a division by zero.
  if (lengthSq === 0) return null;

  const t = Math.min(1, Math.max(0, (ap.x * ab.x + ap.y * ab.y) / lengthSq));
  const dx = ap.x - t * ab.x;
  const dy = ap.y - t * ab.y;
  return { t, metres: Math.sqrt(dx * dx + dy * dy) };
}

/**
 * Whether the position is recent enough to mean anything.
 *
 * Two clocks and no benefit of the doubt. An `expiresAt` that has passed is
 * PTV's own word that the fix is spent. An `at` older than two minutes is the
 * same conclusion reached from the other end. And when NEITHER timestamp parses
 * there is no evidence of freshness at all — which is a refusal, not a pass:
 * an undated coordinate is indistinguishable from an ancient one.
 */
function isCurrent(position, now) {
  const expiresAt = Date.parse(position?.expiresAt ?? '');
  const at = Date.parse(position?.at ?? '');
  const haveExpiry = Number.isFinite(expiresAt);
  const haveAt = Number.isFinite(at);
  if (!haveExpiry && !haveAt) return false;
  if (haveExpiry && expiresAt <= now) return false;
  if (haveAt && now - at > MAX_POSITION_AGE_MS) return false;
  return true;
}

/**
 * Where this train is on its own pattern — or null, which is the common answer.
 *
 * `{ afterIndex, t, phrase }`: `afterIndex` indexes the run's `stops`, and names
 * the station at the START of the winning pair, so a caller can hang a marker
 * off a row it already draws. `t` is 0 at that station and 1 at the next one.
 *
 * PTV's own `direction` field is deliberately NOT read. The pattern is the
 * train's own list of stops in the order it makes them, so which way it is
 * going is already settled by the order of the array; a second opinion could
 * only disagree with it.
 */
export function progressOn(pattern, { now = Date.now() } = {}) {
  const stops = Array.isArray(pattern?.stops) ? pattern.stops : [];

  // THE OPERATOR SAYING THE TRAIN IS THERE BEATS A MISSING FIX. GTFS-Realtime
  // can publish STOPPED_AT without a usable coordinate, or with a timestamp
  // the two-minute window has already refused, and the train card then drew
  // an empty rail for about a poll — the glyph gone until a later vehicle
  // position landed. A stop marked `atPlatform` is the station it is at.
  const incoming = stops.findIndex((s) => s?.incoming && !s?.atPlatform);
  if (incoming >= 0) {
    const name = nameOf(stops[incoming]);
    if (name) {
      return {
        afterIndex: Math.min(Math.max(0, incoming - 1), Math.max(0, stops.length - 2)),
        t: incoming === 0 ? 0 : 0.85,
        atIndex: incoming,
        phrase: `ARRIVING ${name.toUpperCase()}`,
      };
    }
  }

  const at = stops.findIndex((s) => s?.atPlatform);
  if (at >= 0) {
    const name = nameOf(stops[at]);
    if (name) {
      return {
        afterIndex: Math.min(at, Math.max(0, stops.length - 2)),
        t: at > 0 && at === stops.length - 1 ? 1 : 0,
        atIndex: at,
        phrase: `AT ${name.toUpperCase()}`,
      };
    }
  }

  const position = pattern?.position;
  const lat = Number(position?.lat);
  const lng = Number(position?.lng);
  // `Number(null)` is 0, and 0,0 is a point in the Atlantic that would score
  // beautifully against nothing. An absent coordinate is refused, not coerced.
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (!isCurrent(position, now)) return null;

  if (stops.length < 2) return null;

  const point = { lat, lng };
  const scored = [];

  for (let i = 0; i < stops.length - 1; i += 1) {
    const a = coordOf(stops[i]);
    const b = coordOf(stops[i + 1]);
    // A missing coordinate kills THIS pair only. The next iteration starts from
    // the stop that had none, so no chord is ever drawn over the top of it.
    if (!a || !b) continue;
    const hit = projectOnto(a, b, point);
    if (hit) scored.push({ index: i, t: hit.t, metres: hit.metres });
  }

  if (!scored.length) return null;

  scored.sort((x, y) => x.metres - y.metres);
  const [winner, runnerUp] = scored;

  if (winner.metres > MAX_OFF_CHORD_METRES) return null;

  // The loop and the tunnel: two segments of one run lying near-parallel. Two
  // adjacent segments share a station and describe the same piece of railway,
  // so a close second there is agreement rather than doubt.
  if (runnerUp
    && runnerUp.metres <= winner.metres * AMBIGUOUS_RATIO
    && Math.abs(runnerUp.index - winner.index) !== 1) {
    return null;
  }

  const fromStop = stops[winner.index];
  const toStop = stops[winner.index + 1];
  const from = nameOf(fromStop);
  const to = nameOf(toStop);

  // Standing at one end of the pair, which is a third thing to say and not a
  // shade of the other two.
  const end = standingAt(fromStop, toStop, point);
  const atName = end === 'from' ? from : to;
  if (end && atName) {
    return {
      afterIndex: winner.index,
      t: winner.t,
      // The stop it is AT, so a marker can sit ON that row rather than in the
      // gap above or below it.
      atIndex: end === 'from' ? winner.index : winner.index + 1,
      phrase: `AT ${atName.toUpperCase()}`,
    };
  }

  const justLeft = winner.t <= JUST_LEFT_UNTIL;
  const station = justLeft ? from : to;
  // A pair we cannot name is a phrase we cannot write. The marker goes rather
  // than reading "JUST LEFT".
  if (!station) return null;

  return {
    afterIndex: winner.index,
    t: winner.t,
    atIndex: null,
    phrase: `${justLeft ? 'JUST LEFT' : 'APPROACHING'} ${station.toUpperCase()}`,
  };
}

/**
 * Which end of the pair the train is standing at, or null.
 *
 * PTV'S OWN WORD FIRST. `at_platform` on a pattern stop is the operator saying
 * the train is there — better evidence than a coordinate that is up to a
 * carriage's length out, and the only evidence at all for a station whose
 * bundled coordinate we do not have. It is only ever read for a stop the
 * winning segment touches: `at_platform` on a stop four stations away is about
 * a different moment of the same run.
 */
function standingAt(fromStop, toStop, point) {
  const fromPlatform = Boolean(fromStop?.atPlatform);
  const toPlatform = Boolean(toStop?.atPlatform);

  const a = coordOf(fromStop);
  const b = coordOf(toStop);
  const da = a ? metresBetween(a.lat, a.lng, point.lat, point.lng) : Infinity;
  const db = b ? metresBetween(b.lat, b.lng, point.lat, point.lng) : Infinity;

  if (fromPlatform !== toPlatform) return fromPlatform ? 'from' : 'to';
  // Neither stop claims it, so the coordinate decides — and mostly refuses.
  if (!fromPlatform && Math.min(da, db) > AT_STATION_METRES) return null;
  return da <= db ? 'from' : 'to';
}

/**
 * The pattern cut at the stop you board from — and whether to show the half
 * behind you.
 *
 * The train card used to begin at the boarding stop, which was right while it
 * only answered "what is ahead of me". It is wrong for a live position: MOST OF
 * THE TIME SOMEBODY OPENS THIS CARD THE TRAIN HAS NOT REACHED THEM YET, so the
 * marker belongs among the stops the old slice threw away, and a position with
 * nowhere to render is a position nobody sees.
 *
 * So both halves are kept and the one behind you is folded — collapsed by
 * default, because the default question really is "what is ahead of me" — and
 * it OPENS ITSELF when the train is in there. That is the same rule
 * `LineStrip.vue` uses for a group holding a shaded stop: collapsing is worth a
 * row of screen, hiding the answer is not.
 *
 * Pure and exported so the decision can be tested without mounting anything:
 * this repo has no component-test infrastructure, and the fold's whole
 * correctness is in `openByDefault`.
 */
export function boardingSplit(stops, boardingStopId, progress = null) {
  const list = Array.isArray(stops) ? stops : [];
  const found = list.findIndex((s) => Number(s?.stopId) === Number(boardingStopId));
  // A boarding stop the pattern does not contain is not evidence to hide
  // anything by: the whole run is "ahead", exactly as the old slice did.
  const boardingIndex = found >= 0 ? found : 0;

  const at = Number(progress?.afterIndex);
  // The segment that ENDS at the boarding stop starts at boardingIndex - 1,
  // which is the last folded row — so the marker lands inside the fold and the
  // fold has to be open for it to be read.
  const openByDefault = boardingIndex > 0
    && Number.isFinite(at)
    && at < boardingIndex;

  return {
    boardingIndex,
    before: list.slice(0, boardingIndex),
    ahead: list.slice(boardingIndex),
    openByDefault,
  };
}

// ── Every train on one line, as marks on the strip ───────────────────────────
//
// `progressOn` above answers "where is THIS train on its own pattern". This
// answers "where is every train on THIS LINE", which is the same projection
// against a different list of stops: the line's own stations from the bundle,
// city end first, which is the order `LineStrip.vue` draws them in. So an
// `afterIndex` here indexes `lineStops(routeId)` and the strip can hang a
// glyph between two rows it already has.
//
// The refusals are `progressOn`'s, unchanged and non-negotiable. A line has
// five to sixty trains on it and every one of them is a claim; a view that
// drew a glyph wherever the projection was unsure would be sixty chances an
// hour to put a train on a stretch of railway it is not on.

/** How much of a segment a train must move before we believe it moved at all. */
const MIN_MOVE = 0.02;

/**
 * Which way a train went between two readings, or null.
 *
 * 'up' is towards the TOP of the strip, which is the city, which is index
 * zero — every line in the bundle is stored city end first. So a falling
 * `afterIndex + t` is a train coming in.
 *
 * Only used where PTV will not say: in the City Loop its `direction` is
 * "Clockwise" or "Anticlockwise", which describes the loop rather than the
 * strip and cannot be turned into up or down without knowing where on the loop
 * the train is. Two readings answer it directly.
 *
 * `MIN_MOVE` because the projection is not noiseless: two fixes of a STANDING
 * train differ slightly, and an arrow that flickers between up and down every
 * thirty seconds is worse than no arrow. Two per cent of a segment is a few
 * metres of a station spacing — far below anything a moving train does in the
 * thirty seconds between polls.
 */
export function headingFrom(prev, next) {
  const before = Number(prev?.afterIndex) + Number(prev?.t);
  const after = Number(next?.afterIndex) + Number(next?.t);
  if (!Number.isFinite(before) || !Number.isFinite(after)) return null;
  if (after <= before - MIN_MOVE) return 'up';
  if (after >= before + MIN_MOVE) return 'down';
  return null;
}

/**
 * PTV's word for which way this train is pointing, as a direction on the strip.
 *
 * The one place this app reads `vehicle_position.direction` — and it has to.
 * `progressOn` ignores it because a run PATTERN is the train's own stops in
 * the order it makes them, so the array itself says which way it is going. A
 * LINE's stops are just the line, in one fixed order, and a train projected
 * onto them could be going either way along them. The field is the only
 * evidence there is for a train seen once.
 */
function headingOf(direction, where, previous) {
  const word = String(direction ?? '').trim().toLowerCase();
  if (word === 'inbound') return 'up';
  if (word === 'outbound') return 'down';
  // "Clockwise" and "Anticlockwise" in the loop, and anything PTV has not
  // said: inferred from the last reading of the SAME run, or left blank. A
  // blank heading draws a train with no nose, which is the honest picture of a
  // train we cannot say the direction of.
  return previous ? headingFrom(previous, where) : null;
}

/** Compare a destination to a station name on the same terms: no case, no "Station". */
function flatName(name) {
  return String(name ?? '').toLowerCase().replace(/\bstation\b/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Which way a train is going, from where it says it is going.
 *
 * PTV's `direction` word is blank or "Clockwise" for every train in the loop
 * and for some outside it, and inferring from two readings needs the train to
 * have MOVED between them — a train standing at a platform never earns a
 * heading that way, so it sat on the rail's centreline with no nose and,
 * because dead reckoning needs a heading too, never moved off it. The
 * destination is evidence the run always carries: a train "to East Pakenham"
 * is going down the line, a train "to Town Hall" is coming up it.
 *
 * Resolved against this line's own stops first — the destination's index
 * beyond the segment the train is on means down, at or before it means up —
 * and then against the lines this one runs through into: a Cranbourne train
 * "to Sunbury" is coming up through the city. Anything else is null, which
 * draws the honest picture of a train whose direction nobody can say.
 */
export function headingToward(routeId, destination, where) {
  const want = flatName(destination);
  if (!want) return null;
  const stops = lineStops(routeId);
  const index = stops.findIndex((s) => flatName(s.name) === want);
  if (index >= 0) {
    const after = Number(where?.afterIndex);
    if (!Number.isFinite(after)) return null;
    return index > after ? 'down' : 'up';
  }
  for (const c of throughContinuations(routeId)) {
    if (c.stops.some((s) => flatName(s.name) === want)) return 'up';
  }
  return null;
}

/**
 * Every train that can honestly be placed on one line's strip.
 *
 * `trains` is what `fetchTrains` hands back; `previous` is the last array this
 * function returned for the same line, and is how a loop train gets a heading
 * — nothing else is remembered between polls, and nothing is stored.
 *
 * Trains the projection refuses are DROPPED, exactly as the train card drops
 * one. A line view showing eleven of the fourteen trains PTV is tracking is
 * right; a line view showing fourteen, three of them guessed, is not.
 */
export function trainsOnLine(routeId, trains, {
  now = Date.now(), previous = [], stops = lineStops(routeId),
} = {}) {
  if (stops.length < 2) return [];

  const before = new Map();
  for (const row of Array.isArray(previous) ? previous : []) {
    if (row?.runRef) before.set(String(row.runRef), row);
  }

  const out = [];
  for (const train of Array.isArray(trains) ? trains : []) {
    const runRef = train?.runRef == null ? '' : String(train.runRef);
    if (!runRef) continue;

    const where = progressOn({ stops, position: train.position }, { now });
    if (!where) continue;

    // The instant PTV took the fix, as a number. `progressOn` has already
    // refused anything undated or stale, so this is only ever a real recent
    // instant or — for a fix that passed on its expiry alone — null, which
    // `reckon` treats as "nothing to reckon from".
    const fixedAt = Date.parse(train.position?.at ?? '');

    out.push({
      runRef,
      // The line this row was projected onto. A strip drawing a through
      // continuation above its own city block holds trains from two routes,
      // and `afterIndex` is meaningless without knowing whose stops it indexes.
      routeId: Number(routeId),
      destination: String(train.destination ?? '').trim(),
      vehicle: train.vehicle ?? null,
      afterIndex: where.afterIndex,
      t: where.t,
      // Null on all but a train standing at a platform, and the strip draws
      // that one centred on the station's own row.
      atIndex: where.atIndex ?? null,
      phrase: where.phrase,
      // PTV's word first, the destination second, the last reading third.
      heading: headingOf(train.position?.direction, where, before.get(runRef))
        ?? headingToward(routeId, train.destination, where),
      // The evidence behind that, kept apart so `dedupeVehicles` can weigh it.
      wordHeading: headingOf(train.position?.direction, where, null),
      destinationHeading: headingToward(routeId, train.destination, where),
      status: String(train.status ?? ''),
      // The fix itself, as a key: two runs carrying the same coordinate at the
      // same instant are one vehicle.
      fixKey: `${train.position?.lat},${train.position?.lng},${train.position?.at ?? ''}`,
      fixedAt: Number.isFinite(fixedAt) ? fixedAt : null,
    });
  }
  return out;
}

// ── Which way is up, on a strip that is drawn backwards in places ────────────

/**
 * Which lane a glyph rides in and which way its nose points, from where the
 * rows actually are ON SCREEN.
 *
 * `heading` is a word about the LINE — 'up' is towards the city, index zero.
 * But the strip draws a through continuation above its city block with the
 * stops reversed, so on that half a train heading for the city is moving DOWN
 * the screen. The heading word is the wrong thing to point a nose by; the
 * measured rows are the right thing, because they are the picture. So this
 * takes the `top` of the row the train is moving towards and the `top` of the
 * one it left, and answers in screen terms.
 *
 * Two tracks, and Melbourne keeps left: a train moving up the screen takes the
 * left rail and one moving down takes the right, which is what a double track
 * looks like from above, and is what keeps two trains passing each other from
 * being drawn through one another. A train with no known direction has no
 * lane — it is drawn centred between the rails and without a nose, because a
 * lane is a claim about direction too.
 */
export function laneFor({ towardsTop, fromTop } = {}) {
  // Tested for null BEFORE coercion: `Number(null)` is 0, which is a row at
  // the very top of the strip and would point every unmeasured train up.
  const to = towardsTop == null ? NaN : Number(towardsTop);
  const from = fromTop == null ? NaN : Number(fromTop);
  if (!Number.isFinite(to) || !Number.isFinite(from) || to === from) {
    return { facing: null, lane: 'centre' };
  }
  return to < from
    ? { facing: 'up', lane: 'left' }
    : { facing: 'down', lane: 'right' };
}

// ── Dead reckoning between fixes ─────────────────────────────────────────────
//
// PTV reports a position about once a minute and the page polls every thirty
// seconds, so a glyph drawn only from fixes stands still for half a minute
// and then jumps. Between fixes the train is, in fact, moving, and the strip
// can say so honestly as long as it says it as an ESTIMATE and never as a
// fact: the phrase on the card stays PTV's fix, and the estimate is capped
// short of the next station so the glyph never claims an arrival PTV has not
// reported.
//
// THIS IS AN ESTIMATE FROM STATION SPACING, NOT FROM THE TIMETABLE. The exact
// answer — this run's own pattern times — is one PTV call per train, and a
// line has sixty trains on it. So the speed is one nominal figure for the
// whole network, the dwell is one nominal figure, and the segment length is
// the chord between two bundled stations, which is what `progressOn` projects
// onto anyway. A glyph a hundred metres out is an estimate being an estimate;
// a glyph on a chord the train is not on would be a lie, and nothing here
// can produce that because it only ever moves along the segment the fix
// already placed it on, or off a platform the fix already put it at.

/** About 54 km/h: what a suburban train averages between stations. */
export const RECKON_SPEED_MPS = 15;

/** How long a train standing at a platform is assumed to stay there. */
export const RECKON_DWELL_MS = 25_000;

/**
 * The furthest along a segment an estimate may go. A train at 0.92 is drawn
 * approaching the station; only a fix from PTV can put it there.
 */
export const RECKON_MAX_T = 0.92;

/**
 * The segment a train will be reckoned along, and which way along it.
 *
 * `{ afterIndex, sign }` where `afterIndex` indexes the line's stops exactly as
 * the fix's does and `sign` is +1 for a train whose `t` grows (heading 'down',
 * away from the city) or -1 for one whose `t` shrinks. Null where there is
 * nothing to reckon: no heading, or a standing train at the end of the line
 * in the direction it is facing.
 *
 * A standing train leaves ITS platform, not the segment the fix happened to
 * score it on: `progressOn` reports a standing train against whichever of the
 * two touching segments won, which is a coin toss, and a train that pulled
 * into Malvern heading down leaves along Malvern→Armadale whichever segment
 * it was scored on.
 *
 * Exported because the caller has to measure this segment before it can
 * reckon along it: the strip knows the stops and this file knows the rule.
 */
export function reckonSegment(train) {
  const heading = train?.heading;
  if (heading !== 'up' && heading !== 'down') return null;
  const sign = heading === 'down' ? 1 : -1;
  const atIndex = train?.atIndex;
  if (atIndex != null) {
    const at = Number(atIndex);
    if (!Number.isInteger(at)) return null;
    const afterIndex = sign > 0 ? at : at - 1;
    return afterIndex < 0 ? null : { afterIndex, sign };
  }
  const afterIndex = Number(train?.afterIndex);
  return Number.isInteger(afterIndex) ? { afterIndex, sign } : null;
}

/**
 * Where the train probably is now, given where PTV last saw it.
 *
 * `train` is a `trainsOnLine` row: its `fixedAt`, `t`, `atIndex` and `heading`
 * are read. `segmentMetres` is the chord length of `reckonSegment(train)`.
 * Returns `{ afterIndex, t, atIndex, estimated }` in the shape the strip
 * already places — `estimated` is false when the answer IS the fix.
 *
 * The rules, each of which is a refusal to guess further than the evidence:
 *
 *   * Nothing is reckoned from a fix with no timestamp, from the future, or
 *     older than `MAX_POSITION_AGE_MS` — the projection has already refused
 *     the last of those, so the row does not exist, but the rule is stated
 *     here so this function cannot be made to reckon on its own.
 *   * A train with no heading is not moved: an estimate needs a direction,
 *     and moving it either way would be a coin toss drawn as motion.
 *   * A standing train holds for `RECKON_DWELL_MS` from its fix, then pulls
 *     out towards the next stop in its heading. It keeps `atIndex` while it
 *     holds, so the strip goes on centring it on the platform, and loses it
 *     the moment it is estimated to be moving.
 *   * The estimate never runs backwards and never passes `RECKON_MAX_T` of
 *     the segment. Never backwards because a train does not, and never to
 *     the end because an arrival is PTV's to report.
 */
/**
 * A station stop, in seconds, taken off a timetabled gap.
 *
 * The minutes between two consecutive departures in a pattern include the
 * time standing at the second platform; the reckoning wants the time MOVING.
 */
export const RECKON_TIMETABLE_DWELL_S = 30;
/** No gap is reckoned as shorter than this, whatever the timetable says. */
export const RECKON_MIN_TRAVEL_S = 40;

export function reckon(train, { now = Date.now(), segmentMetres, segmentSeconds } = {}) {
  const fix = {
    afterIndex: Number(train?.afterIndex),
    t: Math.min(1, Math.max(0, Number(train?.t) || 0)),
    atIndex: train?.atIndex ?? null,
    estimated: false,
  };

  // Tested for null BEFORE it is coerced: `Number(null)` is 0, a perfectly
  // finite instant in 1970 from which every train would be reckoned to the cap.
  if (train?.fixedAt == null) return fix;
  const fixedAt = Number(train.fixedAt);
  if (!Number.isFinite(fixedAt) || now < fixedAt) return fix;
  // FROZEN past the age the projection accepts, never wound back. A fix that
  // was 100 s old at the poll has been reckoned forward for twenty seconds by
  // the time it turns 120; returning the raw fix here would jump the glyph
  // back up the line for the ten seconds until the next poll drops the train.
  // So the estimate stops where it was and waits to be replaced.
  const age = Math.min(now - fixedAt, MAX_POSITION_AGE_MS);

  const segment = reckonSegment(train);
  if (!segment) return fix;

  // THE TIMETABLE FIRST. `segmentSeconds` is the scheduled gap between the two
  // stops — a run's own pattern on the train card, the line's typical gap on
  // the line page — and a train is expected to take that long, less the stop
  // at the far end. Station spacing at a nominal speed is the fallback for a
  // gap the timetable does not cover.
  const scheduled = Number(segmentSeconds);
  const metres = Number(segmentMetres);
  let travelSeconds = null;
  if (Number.isFinite(scheduled) && scheduled > 0) {
    travelSeconds = Math.max(RECKON_MIN_TRAVEL_S, scheduled - RECKON_TIMETABLE_DWELL_S);
  } else if (Number.isFinite(metres) && metres > 0) {
    travelSeconds = metres / RECKON_SPEED_MPS;
  }
  if (travelSeconds == null) return fix;

  const standing = fix.atIndex != null;
  const moving = standing ? age - RECKON_DWELL_MS : age;
  if (moving <= 0) return fix;

  // The fraction of this segment covered since the train started moving, and
  // the point it started from: the fix itself, or the platform end of the
  // segment it is pulling out of.
  const covered = (moving / 1000) / travelSeconds;
  const start = standing ? (segment.sign > 0 ? 0 : 1) : fix.t;
  const raw = start + segment.sign * covered;
  // Floored at `start`, never at the fix's own `t`: a standing train's fix may
  // have been scored on the OTHER segment touching its platform, and its `t`
  // there says nothing about this one. The cap is measured from the station
  // the train left, whichever end of the segment that is, so a train reckoned
  // up the line stops at 1 - 0.92.
  // The floor is applied LAST: a fix PTV placed past the cap stays where PTV
  // put it rather than being dragged back to a limit meant for estimates.
  const t = segment.sign > 0
    ? Math.max(start, Math.min(RECKON_MAX_T, raw))
    : Math.min(start, Math.max(1 - RECKON_MAX_T, raw));

  return { afterIndex: segment.afterIndex, t, atIndex: null, estimated: true };
}

/**
 * One glyph per vehicle, when PTV has given the vehicle two runs.
 *
 * PTV attaches a vehicle's position to every run that vehicle is rostered
 * for — the one it is on and the one it will do next — so a train at Town
 * Hall arrives as "to Sunbury" AND "to Town Hall" with the same coordinate at
 * the same second, and the strip drew both, noses opposite, one in each lane.
 * The line page also fetches the lines this one runs through into, so the
 * same run can arrive twice from two routes.
 *
 * Grouped by the fix, and the copy kept is the one with the most evidence
 * that it is the CURRENT run: its destination agrees with PTV's own
 * direction word (a train PTV calls Inbound is not the run "to Sunbury"),
 * then a status of "updated" over "scheduled", then the page's own route.
 * Ties keep the first, which is the order PTV listed them.
 */
export function dedupeVehicles(rows, { ownRouteId = null } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const score = (r) => (r.wordHeading && r.destinationHeading === r.wordHeading ? 4 : 0)
    + (r.status === 'updated' ? 2 : 0)
    + (ownRouteId != null && Number(r.routeId) === Number(ownRouteId) ? 1 : 0);
  const keep = new Map();
  for (const row of list) {
    const key = row?.fixKey || `run:${row?.runRef}`;
    const have = keep.get(key);
    if (!have || score(row) > score(have)) keep.set(key, row);
  }
  return list.filter((row) => keep.get(row?.fixKey || `run:${row?.runRef}`) === row);
}

// ── Snap or slide ────────────────────────────────────────────────────────────

/**
 * Whether a glyph should animate its way to its new position, or just be there.
 *
 * THE SLIDE IS ONLY HONEST IN ONE CASE: the same train, measured again, in a
 * layout that has not moved. That is a train driving down the line, and
 * watching it is the whole point of the markers.
 *
 * Every other case is the strip re-drawing itself, and animating those made the
 * app look like it was thinking: opening a fold, arriving on the page, or
 * rotating the phone sent every glyph gliding down from wherever it happened to
 * be — a train that never made that journey. So a first placement snaps, a
 * re-measure snaps, and a train that was not on the last poll snaps.
 *
 * `previous` is the last placement written for this run (`{ runRef }` is all
 * that is read of it) or null; `next` is the one about to be written.
 * `layoutChanged` is the caller's word that the rows themselves moved.
 *
 * Pure and exported so the decision can be tested without mounting anything:
 * this repo has no component-test infrastructure, and a transition that fires
 * when it should not is invisible to every other kind of test.
 */
export function placementMode(previous, next, { layoutChanged = false } = {}) {
  if (layoutChanged) return 'snap';
  const was = previous?.runRef == null ? '' : String(previous.runRef);
  const now = next?.runRef == null ? '' : String(next.runRef);
  // No previous placement is either a train we have never drawn or one that
  // was absent last poll. Both are an appearance, not a journey.
  if (!was || !now || was !== now) return 'snap';
  return 'slide';
}
